import type { ProjectState } from "@livcg/schema";
import { Minus, Pause, Play, Plus, RotateCcw } from "lucide-react";
import { useState } from "react";
import { useLiveClock } from "../../shared/liveClock.js";
import { sendRaw } from "../../shared/sendRaw.js";
import { Button, ConfirmDialog, cn, focusRing } from "../../ui/index.js";
import { PHASE_SHORT } from "../phases.js";
import { extraBadgeTitle, useStagedExtraTime } from "../useStagedExtraTime.js";
import { Panel } from "./Panel.js";

function pad(n: number): string {
  return String(Math.max(0, Math.floor(n))).padStart(2, "0");
}

/** Parse an edit string into (minute, second):
 *  "45:23" → 45,23 · "4523" → 45,23 · "523" → 5,23 · "23" → 23,0 */
function parseEdit(raw: string): { minute: number; second: number } {
  const trimmed = raw.trim();
  let mm = 0;
  let ss = 0;
  if (trimmed.includes(":")) {
    const [mPart = "0", sPart = "0"] = trimmed.split(":");
    mm = Number(mPart) || 0;
    ss = Number(sPart) || 0;
  } else {
    const digits = trimmed.replace(/\D/g, "");
    if (digits.length <= 2) {
      mm = Number(digits) || 0;
    } else {
      mm = Number(digits.slice(0, -2)) || 0;
      ss = Number(digits.slice(-2)) || 0;
    }
  }
  return {
    minute: Math.max(0, Math.min(130, Math.floor(mm))),
    second: Math.max(0, Math.min(59, Math.floor(ss))),
  };
}

type Props = { slug: string; state: ProjectState };

export function ClockControl({ slug, state }: Props) {
  // The clock is a separate subscription — the per-second tick re-renders only this panel.
  const m = useLiveClock(slug);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);
  const extra = useStagedExtraTime(slug, state);

  const beginEdit = () => {
    setEditValue(`${pad(m.minute)}:${pad(m.second)}`);
    setEditing(true);
  };
  const commitEdit = () => {
    const { minute, second } = parseEdit(editValue);
    void sendRaw("clock.set", {
      slug,
      minute,
      second,
      phase: m.phase,
      additionalTime: m.additionalTime,
      running: m.running,
    });
    setEditing(false);
  };

  const toggle = () => void sendRaw("clock.toggle", { slug });
  const adjust = (deltaSeconds: number) => void sendRaw("clock.adjust", { slug, deltaSeconds });
  const doReset = () => {
    void sendRaw("clock.set", {
      slug,
      minute: 0,
      second: 0,
      phase: m.phase,
      additionalTime: 0,
      running: false,
    });
  };

  return (
    <Panel label="Vaqt">
      {/* Clock readout — click to edit. Centered, large, tabular. */}
      <div className="flex flex-col items-center gap-3">
        {editing ? (
          <input
            type="text"
            inputMode="numeric"
            value={editValue}
            // Edit is opened by a deliberate click on the readout, so focusing
            // the field immediately is the expected behaviour, not a surprise.
            // biome-ignore lint/a11y/noAutofocus: user-triggered inline edit
            autoFocus
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitEdit();
              }
              if (e.key === "Escape") setEditing(false);
            }}
            onBlur={commitEdit}
            placeholder="MM:SS"
            title='"45:23" yoki "4523" · Enter — saqlash · Esc — bekor'
            className="box-border h-[60px] w-full rounded-md border-2 border-accent-strong bg-surface px-2 py-0 text-center font-display text-[52px] leading-[60px] font-bold tabular-nums tracking-[-0.04em] focus-visible:outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={beginEdit}
            title="Vaqtni o'zgartirish uchun bosing"
            className={cn(
              "font-display text-[52px] leading-none font-bold tabular-nums tracking-[-0.03em] transition-colors hover:text-accent-strong",
              focusRing,
            )}
          >
            <span>{pad(m.minute)}</span>
            <span className={cn("mx-0.5", m.running ? "text-live" : "text-ink-faint")}>:</span>
            <span>{pad(m.second)}</span>
          </button>
        )}

        <span className="rounded bg-surface-elevated px-2 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] tabular-nums text-ink-soft">
          {PHASE_SHORT[m.phase]}
        </span>

        {/* Start / pause is the single most-used control — full width, large. */}
        <Button
          variant={m.running ? "secondary" : "success"}
          size="lg"
          className="w-full"
          onClick={toggle}
          leadingIcon={m.running ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        >
          {m.running ? "Pauza" : "Start"} <span className="ml-1 opacity-50">(Space)</span>
        </Button>

        {/* Adjust seconds */}
        <div className="grid w-full grid-cols-6 gap-1">
          {[-10, -5, -1, 1, 5, 10].map((d) => (
            <Button
              key={d}
              variant="secondary"
              size="sm"
              className="font-mono text-ink-soft"
              onClick={() => adjust(d)}
            >
              {d > 0 ? `+${d}` : d}
            </Button>
          ))}
        </div>

        {/* Extra time + reset */}
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10.5px] uppercase tracking-[0.08em] text-ink-muted">Extra</span>
            <Button
              variant="secondary"
              size="xs"
              iconOnly
              aria-label="extra-vaqtni kamaytirish"
              disabled={extra.draft <= 0}
              onClick={extra.dec}
            >
              <Minus className="h-3 w-3" />
            </Button>
            <button
              type="button"
              onClick={extra.send}
              disabled={!extra.canSend}
              title={extraBadgeTitle(extra.badge, extra.draft)}
              aria-label={extraBadgeTitle(extra.badge, extra.draft)}
              className={cn(
                "min-w-[34px] rounded-md px-2 py-0.5 text-center text-[12px] font-medium tabular-nums transition-colors",
                focusRing,
                extra.badge === "staged" &&
                  "cursor-pointer bg-paused/25 text-paused ring-2 ring-paused/70 animate-pulse",
                extra.badge === "sent" && "bg-paused/15 text-paused",
                extra.badge === "live" && "bg-live/20 text-live",
                extra.badge === "empty" && "bg-surface-elevated text-ink-muted",
              )}
            >
              +{extra.draft}
            </button>
            <Button
              variant="secondary"
              size="xs"
              iconOnly
              aria-label="extra-vaqtni oshirish"
              onClick={extra.inc}
            >
              <Plus className="h-3 w-3" />
            </Button>
            {/* Turn the strip on/off without the banner — so the banner doesn't come up
                again when the value is corrected (also used to take it off when the
                referee lowers the board). */}
            <label
              className={cn(
                "flex cursor-pointer items-center gap-1 text-[10.5px] text-ink-muted",
                extra.draft <= 0 && "cursor-not-allowed opacity-40",
              )}
              title="Soat ostidagi strip efirda turadimi (banner chiqmaydi)"
            >
              <input
                type="checkbox"
                className="h-3 w-3 accent-live"
                checked={extra.onAir}
                disabled={extra.draft <= 0}
                onChange={(e) => extra.setOnAir(e.target.checked)}
              />
              Strip
            </label>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmReset(true)}
            leadingIcon={<RotateCcw className="h-3.5 w-3.5" />}
          >
            Reset
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmReset}
        title="Vaqtni resetlash"
        description={`Vaqtni ${pad(m.minute)}:${pad(m.second)}'dan 00:00 ga olib tashlashni tasdiqlaysizmi?\nBu amalni qaytarib bo'lmaydi.`}
        confirmLabel="Resetlash"
        onConfirm={() => {
          setConfirmReset(false);
          doReset();
        }}
        onCancel={() => setConfirmReset(false)}
      />
    </Panel>
  );
}
